import { useRef, useState } from 'react';
import { Col, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { AiOutlineClose } from 'react-icons/ai';
import { BiPlus, BiSolidBot, BiSolidRocket, BiX } from 'react-icons/bi';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { resetCreateFlow, setType, toggeleCreateModal } from '@/features/scenario/create/create.slice';
import Utilities from '@/utils';

import { useAppDispatch, useAppSelector } from '../../app/hook';
import { MultiLanguageUrl } from '../../constants/multiLanguage.constant';
import usePermission from '../../hooks/usePermission';
import { BotTypeEnum } from '../../utils/BotTypeEnum';
import { HumanCloneFillIcon } from '../common/icons/humanClone.icon';
import { useOnClickOutside } from '../common/PopoverWithAudio';
import { PSButton } from '../ui/button';

type Props = {
  className?: string;
  isShow?: boolean;
};

function PluseMenuComponent({ isShow = false, className }: Props) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const authState = useAppSelector(state => state.authState);
  const [showMenu, setShowMenu] = useState(false);
  const [showLogOutPopUp, setShowLogOutPopUp] = useState(false);
  const dispatch = useAppDispatch();
  const permissions = usePermission();

  const canCreateMore = Utilities.useCanCreateMoreChatbots();
  const closeMenu = () => {
    setShowMenu(false);
  };
  const ref = useRef(null);

  useOnClickOutside(ref, () => {
    setShowMenu(false);
  });

  return (
    <>
      <div className={`tw-w-8 tw-h-8 tw-bg-black/opacity-25 tw-rounded-full tw-inline-flex ${!isShow && 'tw-hidden'} tw-relative ${className}`}>
        <div
          role="button"
          tabIndex={0}
          className="tw-cursor-pointer"
          onClick={() => {
            setShowMenu(prev => !prev);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              setShowMenu(prev => !prev);
            }
          }}
        >
          <div
            className={`tw-w-8 tw-h-8 tw-flex  tw-bg-primary tw-text-white tw-rounded-[19px] tw-shadow tw-justify-center tw-items-center md:tw-inline-flex ${
              !isShow && 'tw-hidden'
            }`}
          >
            {showMenu && <BiX size={20} />}
            {!showMenu && <BiPlus size={20} />}
          </div>
        </div>

        {showMenu && (
          <div ref={ref} className="tw-relative ">
            <div className="tw-absolute tw-top-10 tw-right-0 tw-flex tw-flex-col tw-z-50">
              <div className="tw-w-full tw-overflow-hidden tw-inline-block tw-relative tw-h-3 ">
                <div className=" tw-h-3 tw-w-3 tw-bg-primary tw-rotate-45  tw-transform tw-origin-bottom-left tw-absolute tw-right-4"></div>
              </div>
              <div className="tw-w-[244px] tw-h-[88px] tw-relative">
                <div className="tw-w-[244px] tw-h-[88px] tw-left-0  tw-absolute tw-bg-primary tw-rounded-md tw-shadow tw-flex-col tw-justify-start tw-items-start tw-inline-flex">
                  <div className="tw-self-stretch tw-h-[88px] tw-py-2 tw-flex-col tw-justify-start tw-items-start tw-flex">
                    <div className="tw-self-stretch tw-px-2 tw-justify-start tw-items-center tw-gap-3 tw-inline-flex">
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          if (permissions?.widgetPage?.addNewBot) {
                            dispatch(resetCreateFlow());
                            dispatch(toggeleCreateModal(true));
                          } else {
                            setShowMenu(false);
                            toast.warn(t('HelpBanner/ChatbotCreation/maxBotReachedMessage'));
                          }
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            if (permissions?.widgetPage?.addNewBot) {
                              dispatch(resetCreateFlow());
                              dispatch(toggeleCreateModal(true));
                            } else {
                              setShowMenu(false);
                              toast.warn(t('HelpBanner/ChatbotCreation/maxBotReachedMessage'));
                            }
                          }
                        }}
                        className="tw-grow shrink tw-basis-0 tw-h-9 tw-p-2 tw-cursor-pointer hover:tw-bg-blue-700 tw-rounded-md tw-justify-start tw-items-center tw-gap-3 tw-flex"
                      >
                        <BiSolidBot size={22} className="tw-text-white" />
                        <div className="tw-text-gray-100 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{t('Wizard/general/key3')}</div>
                        {!permissions?.widgetPage?.addNewBot && (
                          <div className="tw-w-4 tw-h-4 tw-p-0.5 tw-bg-amber-300 tw-rounded-[20px] tw-shadow tw-justify-center tw-items-center tw-inline-flex">
                            <BiSolidRocket className="tw-text-stone-700" size={13} />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="tw-self-stretch tw-px-2 tw-justify-start tw-items-center tw-gap-3 tw-inline-flex">
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          if (permissions?.extensionPage?.addNewBot && canCreateMore) {
                            dispatch(resetCreateFlow());
                            dispatch(toggeleCreateModal(true));
                            dispatch(setType(BotTypeEnum.HUMAN_CLONE));
                          } else {
                            navigate(`/${MultiLanguageUrl[i18n.language]}/control-panel/billing-create`);
                          }
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            if (permissions?.extensionPage?.addNewBot && canCreateMore) {
                              dispatch(resetCreateFlow());
                              dispatch(toggeleCreateModal(true));
                              dispatch(setType(BotTypeEnum.HUMAN_CLONE));
                            } else {
                              navigate(`/${MultiLanguageUrl[i18n.language]}/control-panel/billing-create`);
                            }
                          }
                        }}
                        className="tw-grow shrink tw-basis-0 tw-h-9 tw-p-2 tw-cursor-pointer hover:tw-bg-blue-700 tw-rounded-md tw-justify-start tw-items-center tw-gap-3 tw-flex"
                      >
                        <HumanCloneFillIcon />
                        <div className="tw-text-gray-100 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{t('Wizard/general/key4')}</div>
                        {!permissions?.extensionPage?.addNewBot && (
                          <div className="tw-w-4 tw-h-4 tw-p-0.5 tw-bg-amber-300 tw-rounded-[20px] tw-shadow tw-justify-center tw-items-center tw-inline-flex">
                            <BiSolidRocket className="tw-text-stone-700" size={13} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Logout modal */}
      <Modal show={showLogOutPopUp} centered onHide={() => setShowLogOutPopUp(false)}>
        <Row className="m-0">
          <Col className="tw-pt-4 tw-ps-4">
            <AiOutlineClose className="tw-cursor-pointer" onClick={() => setShowLogOutPopUp(false)} />
          </Col>
        </Row>
        <Modal.Body className="text-center tw-px-14 tw-pb-14 tw-pt-20 ">
          <Row>
            <Col className="tw-flex  tw-justify-center">
              <div className="tw-border-solid tw-w-20 tw-h-20 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-border-slate-50 tw-border-[12px]">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_2596_3315)">
                    <path
                      d="M17.964 3.13288C17.766 2.78801 17.4806 2.50148 17.1364 2.30223C16.7923 2.10297 16.4017 1.99805 16.004 1.99805C15.6064 1.99805 15.2157 2.10297 14.8716 2.30223C14.5275 2.50148 14.242 2.78801 14.044 3.13288L0.330014 26.4669C-0.583986 28.0229 0.512014 30.0009 2.29001 30.0009H29.716C31.494 30.0009 32.592 28.0209 31.676 26.4669L17.964 3.13288ZM16 10.0009C17.07 10.0009 17.908 10.9249 17.8 11.9909L17.1 19.0049C17.0765 19.2804 16.9504 19.5371 16.7467 19.7241C16.543 19.9112 16.2766 20.015 16 20.015C15.7235 20.015 15.457 19.9112 15.2533 19.7241C15.0496 19.5371 14.9235 19.2804 14.9 19.0049L14.2 11.9909C14.1749 11.7393 14.2027 11.4853 14.2817 11.2452C14.3607 11.0051 14.4891 10.7842 14.6587 10.5967C14.8283 10.4092 15.0352 10.2594 15.2663 10.1567C15.4973 10.0541 15.7472 10.001 16 10.0009ZM16.004 22.0009C16.5344 22.0009 17.0432 22.2116 17.4182 22.5867C17.7933 22.9617 18.004 23.4704 18.004 24.0009C18.004 24.5313 17.7933 25.04 17.4182 25.4151C17.0432 25.7902 16.5344 26.0009 16.004 26.0009C15.4736 26.0009 14.9649 25.7902 14.5898 25.4151C14.2147 25.04 14.004 24.5313 14.004 24.0009C14.004 23.4704 14.2147 22.9617 14.5898 22.5867C14.9649 22.2116 15.4736 22.0009 16.004 22.0009Z"
                      fill="#FDB139"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_2596_3315">
                      <rect width="32" height="32" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </div>
            </Col>
          </Row>
          <Row className="tw-mt-8">
            <Col>
              <h2 className="font-sofiaSans tw-text-sm ">{t('DashboardMenu/l-08')}</h2>
            </Col>
          </Row>
          <Row>
            <Col>
              <p className="font-sofiaSans  tw-text-zinc-500 ">{t('DashboardMenu/text-01')}</p>
            </Col>
          </Row>
          <Row>
            <Col>
              <Link to={`/${MultiLanguageUrl[i18n.language] || 'en-US'}/logout`} className="btn btn-danger tw-bg-rose-50 tw-flex tw-justify-center">
                {t('DashboardMenu/l-08')}
              </Link>
            </Col>
          </Row>
          <Row className="tw-mt-5">
            <Col>
              <PSButton
                id="btn-logout-popup"
                variant="primary"
                className="w-100 tw-flex tw-justify-center"
                onClick={() => setShowLogOutPopUp(false)}
                title={t('DashboardMenu/l-09')}
              />
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default PluseMenuComponent;
